/**
 * In-place picture cropping (Publisher "Crop" mode).
 *
 * A picture object is a frame (x, y, width, height) showing a picture. Without a crop the picture is laid into the
 * frame with CSS object-fit (cover / contain / fill). With a crop, `properties.crop = { x, y, w, h }` is the picture's
 * own rectangle relative to the frame's top-left corner in unscaled page pixels: whatever falls outside the frame is
 * cropped away. Dragging a crop handle moves an edge of the frame while the picture stays where it is on the page;
 * dragging the picture moves it inside the frame.
 */

export const hasCrop = (p) => !!(p && p.crop && Number.isFinite(p.crop.x) && Number.isFinite(p.crop.y) && p.crop.w > 0 && p.crop.h > 0);

/** Picture rectangle (relative to the frame) that reproduces an object-fit rendering of a natW × natH picture. */
export const fitRect = (frameW, frameH, natW, natH, fit = 'cover') => {
  if (!natW || !natH || fit === 'fill' || fit === 'stretch') return { x: 0, y: 0, w: frameW, h: frameH };
  const k = fit === 'contain' ? Math.min(frameW / natW, frameH / natH) : Math.max(frameW / natW, frameH / natH);
  const w = natW * k, h = natH * k;
  return { x: (frameW - w) / 2, y: (frameH - h) / 2, w, h };
};

const sizeCache = new Map();

/** Natural size of a picture (cached per src). Resolves to null when it cannot be loaded. */
export const loadNaturalSize = (src) => {
  if (!src) return Promise.resolve(null);
  if (sizeCache.has(src)) return Promise.resolve(sizeCache.get(src));
  return new Promise((resolve) => {
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => { const s = { w: img.naturalWidth || img.width, h: img.naturalHeight || img.height }; sizeCache.set(src, s); resolve(s); };
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * Move the frame edges by local (unrotated) deltas while the picture stays put on the page.
 * `edges` = { l, t, r, b } in page px (positive = towards +x / +y). Returns the object patch { x, y, width, height, crop }.
 */
export const resizeFrameKeepPicture = (object, edges, minSize = 8) => {
  const w = object.width, h = object.height;
  const crop = object.properties?.crop || { x: 0, y: 0, w, h };
  let L = -w / 2 + (edges.l || 0), R = w / 2 + (edges.r || 0), T = -h / 2 + (edges.t || 0), B = h / 2 + (edges.b || 0);
  if (R - L < minSize) { if (edges.l) L = R - minSize; else R = L + minSize; }
  if (B - T < minSize) { if (edges.t) T = B - minSize; else B = T + minSize; }
  const rot = ((object.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  // new centre (local → global)
  const lcx = (L + R) / 2, lcy = (T + B) / 2;
  const gcx = object.x + w / 2 + lcx * cos - lcy * sin;
  const gcy = object.y + h / 2 + lcx * sin + lcy * cos;
  const width = R - L, height = B - T;
  // the picture keeps its page position: shift its frame-relative rectangle by how much the top-left edge moved
  const dl = L + w / 2, dt = T + h / 2;
  return {
    x: r2(gcx - width / 2),
    y: r2(gcy - height / 2),
    width: r2(width),
    height: r2(height),
    crop: { x: r2(crop.x - dl), y: r2(crop.y - dt), w: r2(crop.w), h: r2(crop.h) },
  };
};

/** Move the picture inside its frame by local deltas. */
export const movePictureInFrame = (object, dx, dy) => {
  const crop = object.properties?.crop || { x: 0, y: 0, w: object.width, h: object.height };
  return { x: r2(crop.x + dx), y: r2(crop.y + dy), w: crop.w, h: crop.h };
};

/** Publisher "Clear Crop": the frame grows to show the whole picture again (rotation preserved). */
export const clearCropPatch = (object) => {
  const crop = object.properties?.crop;
  if (!crop) return null;
  const w = object.width, h = object.height;
  const rot = ((object.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const dcx = crop.x + crop.w / 2 - w / 2, dcy = crop.y + crop.h / 2 - h / 2;
  const gcx = object.x + w / 2 + dcx * cos - dcy * sin;
  const gcy = object.y + h / 2 + dcx * sin + dcy * cos;
  return { x: r2(gcx - crop.w / 2), y: r2(gcy - crop.h / 2), width: r2(crop.w), height: r2(crop.h), crop: { x: 0, y: 0, w: r2(crop.w), h: r2(crop.h) } };
};

/** Size the picture to the frame (fit = contain, fill = cover) without moving the frame. */
export const refitCrop = (object, nat, fit) => fitRect(object.width, object.height, nat?.w, nat?.h, fit);

const sameCrop = (a, b) => !!a && !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/**
 * Resizing the frame OUTSIDE crop mode (selection handles, Format Picture ▸ Size, the ribbon's Height / Width, the
 * Properties pane, ungrouping) scales the picture with it, as Publisher does: the cropped picture keeps its place and
 * proportions inside the frame. `obj` is the picture before the change, `data` the patch about to be merged into it.
 * Returns the merged object. The crop is left alone when the patch itself sets a different crop (Clear Crop, Fit, Fill),
 * when `keepCrop` is set (crop mode: the frame edge moves, the picture stays on the page) or when the size is unchanged.
 */
export const withCropScaled = (obj, data, keepCrop = false) => {
  const next = { ...obj, ...data };
  if (keepCrop || obj.type !== 'image' || !hasCrop(obj.properties)) return next;
  const oldCrop = obj.properties.crop;
  const newCrop = next.properties?.crop;
  if (!sameCrop(oldCrop, newCrop)) return next;
  const w0 = obj.width, h0 = obj.height, w1 = next.width, h1 = next.height;
  if (!(w0 > 0 && h0 > 0 && w1 > 0 && h1 > 0) || (w1 === w0 && h1 === h0)) return next;
  const sx = w1 / w0, sy = h1 / h0;
  return { ...next, properties: { ...next.properties, crop: { x: r2(oldCrop.x * sx), y: r2(oldCrop.y * sy), w: r2(oldCrop.w * sx), h: r2(oldCrop.h * sy) } } };
};

/** Crop handle geometry: the eight handles and how each one moves the frame edges. */
export const CROP_HANDLES = [
  { id: 'nw', ex: -1, ey: -1, cursor: 'nwse-resize' }, { id: 'n', ex: 0, ey: -1, cursor: 'ns-resize' }, { id: 'ne', ex: 1, ey: -1, cursor: 'nesw-resize' },
  { id: 'w', ex: -1, ey: 0, cursor: 'ew-resize' }, { id: 'e', ex: 1, ey: 0, cursor: 'ew-resize' },
  { id: 'sw', ex: -1, ey: 1, cursor: 'nesw-resize' }, { id: 's', ex: 0, ey: 1, cursor: 'ns-resize' }, { id: 'se', ex: 1, ey: 1, cursor: 'nwse-resize' },
];

/* ------------------------------------------------------------------------------------------------------------------ */
/* Aspect ratios (Crop ▸ Aspect Ratio) — the frame snaps to these while a crop handle is dragged, or is locked to one  */
/* ------------------------------------------------------------------------------------------------------------------ */

/** The presets offered in Crop ▸ Aspect Ratio; `ratio` = width / height (null = free). */
export const CROP_ASPECTS = [
  { id: 'free', label: 'Free', name: 'Free — no fixed ratio', ratio: null },
  { id: '1:1', label: '1:1', name: 'Square 1:1', ratio: 1 },
  { id: '4:3', label: '4:3', name: 'Landscape 4:3', ratio: 4 / 3 },
  { id: '3:4', label: '3:4', name: 'Portrait 3:4', ratio: 3 / 4 },
  { id: '16:9', label: '16:9', name: 'Widescreen 16:9', ratio: 16 / 9 },
  { id: '9:16', label: '9:16', name: 'Tall 9:16', ratio: 9 / 16 },
  { id: '3:2', label: '3:2', name: 'Photo 3:2', ratio: 3 / 2 },
  { id: '2:3', label: '2:3', name: 'Portrait photo 2:3', ratio: 2 / 3 },
];
export const cropAspectById = (id) => CROP_ASPECTS.find((a) => a.id === id) || CROP_ASPECTS[0];

/** How close (relative) a free-dragged frame must come to a preset before it snaps to it. */
export const CROP_SNAP_TOLERANCE = 0.03;
/** Frames smaller than this (page px, either side) never snap — a tiny frame hits every ratio. */
const SNAP_MIN_SIDE = 24;

/** The preset a w × h frame is within the snap tolerance of (null when none, or when the frame is tiny). */
export const nearestCropAspect = (w, h, tolerance = CROP_SNAP_TOLERANCE) => {
  if (!(w >= SNAP_MIN_SIDE && h >= SNAP_MIN_SIDE)) return null;
  const r = w / h;
  let best = null, bestErr = tolerance;
  CROP_ASPECTS.forEach((a) => {
    if (!a.ratio) return;
    const err = Math.abs(r - a.ratio) / a.ratio;
    if (err <= bestErr) { best = a; bestErr = err; }
  });
  return best;
};

/** A short label for any ratio: a preset's label when it is one, else "1.37:1". */
export const cropRatioLabel = (ratio) => {
  if (!(ratio > 0)) return '';
  const preset = CROP_ASPECTS.find((a) => a.ratio && Math.abs(a.ratio - ratio) / a.ratio < 0.005);
  return preset ? preset.label : `${Math.round(ratio * 100) / 100}:1`;
};

/**
 * Apply an aspect ratio to the edge deltas of a crop-handle drag, before resizeFrameKeepPicture turns them into the
 * frame patch. Two modes:
 *  • lock (`ratio` given — Crop ▸ Aspect Ratio, or Shift held): the frame keeps that ratio. A corner follows the
 *    mouse's dominant axis with the opposite corner anchored; a side handle drives its own dimension and the other
 *    one follows, centred on the frame.
 *  • snap (`ratio` null, `snap` true): when the free frame comes within CROP_SNAP_TOLERANCE of a preset, the dragged
 *    edge moves the little extra to hit it exactly (the other edges stay where the user put them).
 * Returns { edges, aspect } — `aspect` is the preset / ratio in force (for the on-canvas badge), null when free.
 */
export const constrainCropEdges = (object, edges, handleId, { ratio = null, snap = true, minSize = 8 } = {}) => {
  const hnd = CROP_HANDLES.find((hh) => hh.id === handleId);
  if (!hnd) return { edges, aspect: null };
  const w = object.width, h = object.height;
  let L = -w / 2 + (edges.l || 0), R = w / 2 + (edges.r || 0), T = -h / 2 + (edges.t || 0), B = h / 2 + (edges.b || 0);
  if (R - L < minSize) { if (hnd.ex < 0) L = R - minSize; else R = L + minSize; }
  if (B - T < minSize) { if (hnd.ey < 0) T = B - minSize; else B = T + minSize; }
  const W = R - L, H = B - T;
  let target = ratio > 0 ? ratio : null, aspect = null, mode = 'lock';
  if (target) aspect = CROP_ASPECTS.find((a) => a.ratio && Math.abs(a.ratio - target) / a.ratio < 0.005) || { id: 'locked', label: cropRatioLabel(target), ratio: target };
  else if (snap) { const near = nearestCropAspect(W, H); if (near) { target = near.ratio; aspect = near; mode = 'snap'; } }
  if (!target) return { edges: { l: L + w / 2, r: R - w / 2, t: T + h / 2, b: B - h / 2 }, aspect: null };
  const setH = (H2) => { if (hnd.ey < 0) T = B - H2; else if (hnd.ey > 0) B = T + H2; else { const cy = (T + B) / 2; T = cy - H2 / 2; B = cy + H2 / 2; } };
  const setW = (W2) => { if (hnd.ex < 0) L = R - W2; else if (hnd.ex > 0) R = L + W2; else { const cx = (L + R) / 2; L = cx - W2 / 2; R = cx + W2 / 2; } };
  if (hnd.ex !== 0 && hnd.ey !== 0) {
    // corner: snapping nudges the vertical edge; a lock follows whichever axis the mouse moved more
    if (mode === 'snap' || Math.abs(W - w) >= Math.abs(H - h) * target) setH(Math.max(minSize, W / target));
    else setW(Math.max(minSize, H * target));
  } else if (hnd.ex !== 0) {
    if (mode === 'snap') setW(Math.max(minSize, H * target)); else setH(Math.max(minSize, W / target));
  } else if (mode === 'snap') setH(Math.max(minSize, W / target));
  else setW(Math.max(minSize, H * target));
  return { edges: { l: L + w / 2, r: R - w / 2, t: T + h / 2, b: B - h / 2 }, aspect };
};

/**
 * Crop ▸ Aspect Ratio ▸ preset: the frame becomes the largest rectangle of that ratio that fits inside it, centred, and
 * the picture stays where it is on the page (PowerPoint's Crop ▸ Aspect Ratio). Returns the object patch, or null when
 * the frame already has the ratio.
 */
export const cropToAspect = (object, ratio) => {
  if (!(ratio > 0) || !(object.width > 0) || !(object.height > 0)) return null;
  const W = object.width, H = object.height;
  if (Math.abs(W / H - ratio) / ratio < 0.0005) return null;
  const W2 = W / H > ratio ? H * ratio : W, H2 = W / H > ratio ? H : W / ratio;
  const dw = (W - W2) / 2, dh = (H - H2) / 2;
  return resizeFrameKeepPicture(object, { l: dw, r: -dw, t: dh, b: -dh });
};
